/**
 * Drop a title's ATX closing run (`## Title ##`). Walks back from the end
 * rather than replacing `/#+$/`, which backtracks quadratically over a long
 * `#` run that turns out not to be at the end.
 */
export const stripClosingHashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === '#') end -= 1;
  return value.slice(0, end);
};

// Title must start with a non-space (`\S`), so `\s+` and the capture can't
// both match the same whitespace — no overlap, stays linear. Any ATX closing
// `#` sequence is stripped from the captured title by the caller.
export const HEADING = /^\s{0,3}#{1,6}\s+(\S.*)$/;

// A whole line of bold text, optionally with a trailing colon. Anchored at
// both ends so `**Note:** some prose` stays body text rather than a section.
export const BOLD_TITLE = /^\s{0,3}\*\*(\S.*?)\*\*:?\s*$/;

/** Swap a line's outermost `from` delimiters for `to` (`**X**` ↔ `__X__`). */
const swapOuterDelimiters = (line: string, from: string, to: string): string => {
  const open = line.indexOf(from);
  const close = line.lastIndexOf(from);
  if (open < 0 || close < open + from.length) return line;
  return `${line.slice(0, open)}${to}${line.slice(open + from.length, close)}${to}${line.slice(close + from.length)}`;
};

/**
 * Escape body lines that would re-parse as section titles. A heading typed
 * into a point's text would otherwise switch a bold or prose summary to the
 * heading dialect, and the viewer would drop everything above it. A bold line
 * would split a bold-dialect point in two, so it is rewritten as `__X__`,
 * which renders the same.
 */
export const escapeBodyHeadings = (body: string): string =>
  body
    .split('\n')
    .map((line) => {
      if (HEADING.test(line)) return line.replace('#', '\\#');
      if (BOLD_TITLE.test(line)) return swapOuterDelimiters(line, '**', '__');
      return line;
    })
    .join('\n');

/** Inverse of `escapeBodyHeadings`, so the editor shows what the user typed. */
export const unescapeBodyHeadings = (body: string): string =>
  body
    .split('\n')
    .map((line) => {
      const unescapedHeading = line.replace('\\#', '#');
      if (unescapedHeading !== line && HEADING.test(unescapedHeading)) {
        return unescapedHeading;
      }
      const unescapedBold = swapOuterDelimiters(line, '__', '**');
      return unescapedBold !== line && BOLD_TITLE.test(unescapedBold)
        ? unescapedBold
        : line;
    })
    .join('\n');
